import type { FinanceApplicationWithRelations } from '@repo/shared-types';

interface Props {
  application: FinanceApplicationWithRelations;
}

const Row = ({ label, value }: { label: string; value?: string | number | null }) => {
  if (value === null || value === undefined || value === '') return null;
  return (
    <div className="border-border/50 flex justify-between gap-4 border-b py-2 last:border-0">
      <span className="text-muted-foreground text-xs">{label}</span>
      <span className="text-foreground text-right text-sm font-medium">{value}</span>
    </div>
  );
};

const Section = ({ title, children }: { title: string; children: React.ReactNode }) => {
  // Hide an entire section when every Row inside resolved to null.
  const hasContent = Array.isArray(children) ? children.some(Boolean) : Boolean(children);
  if (!hasContent) return null;
  return (
    <div>
      <p className="text-muted-foreground mb-1 text-[11px] font-semibold tracking-wide uppercase">
        {title}
      </p>
      <div className="rounded-lg border px-3">{children}</div>
    </div>
  );
};

const duration = (years?: number | null, months?: number | null) => {
  const parts: string[] = [];
  if (typeof years === 'number') parts.push(`${years}y`);
  if (typeof months === 'number') parts.push(`${months}m`);
  return parts.length ? parts.join(' ') : null;
};

const money = (n?: number | null) =>
  typeof n === 'number' ? `£${n.toLocaleString('en-GB')}` : null;

export const FinanceApplicationDetails = ({ application: a }: Props) => {
  const fullName = [a.title, a.forename, a.middle_name, a.surname].filter(Boolean).join(' ');
  const address = [
    a.house_name_number,
    a.address_line_1,
    a.address_line_2,
    a.address_line_3,
    a.town_city,
    a.county,
    a.postcode,
  ]
    .filter(Boolean)
    .join(', ');

  return (
    <div className="space-y-4">
      <Section title="Vehicle">
        <Row label="Vehicle" value={a.vehicle_name} />
        {a.vehicle_vdp_url ? (
          <div className="border-border/50 flex justify-between gap-4 border-b py-2 last:border-0">
            <span className="text-muted-foreground text-xs">Listing</span>
            <a
              href={a.vehicle_vdp_url}
              target="_blank"
              rel="noreferrer"
              className="text-primary text-right text-sm font-medium underline"
            >
              View car
            </a>
          </div>
        ) : null}
      </Section>

      <Section title="Personal">
        <Row label="Name" value={fullName || a.applicant_name} />
        <Row label="Date of birth" value={a.date_of_birth} />
        <Row label="Gender" value={a.gender} />
        <Row label="Nationality" value={a.nationality} />
        <Row label="Marital status" value={a.marital_status} />
        <Row label="Dependants" value={a.dependants} />
        <Row label="Phone" value={a.primary_telephone} />
        <Row label="Secondary phone" value={a.secondary_telephone} />
        <Row label="Email" value={a.email} />
        <Row
          label="Business use"
          value={
            a.purchasing_for_business_use === null || a.purchasing_for_business_use === undefined
              ? null
              : a.purchasing_for_business_use
                ? 'Yes'
                : 'No'
          }
        />
      </Section>

      <Section title="Current address">
        <Row label="Address" value={address} />
        <Row label="Time at address" value={duration(a.years_at_address, a.months_at_address)} />
        <Row label="Accommodation" value={a.accommodation_status} />
      </Section>

      <Section title="Employment">
        <Row label="Occupation" value={a.occupation} />
        <Row label="Employer" value={a.employer} />
        <Row label="Employer phone" value={a.employer_phone} />
        <Row label="Basis" value={a.employment_basis} />
        <Row label="Category" value={a.employment_category} />
        <Row
          label="Time at employment"
          value={duration(a.years_at_employment, a.months_at_employment)}
        />
        <Row label="Gross annual salary" value={money(a.gross_annual_salary)} />
      </Section>

      <Section title="Notes">
        <Row label="Notes" value={a.notes} />
      </Section>
    </div>
  );
};
